/**
 * Language catalogue. No language is hard-coded anywhere else in the platform:
 * every interview picks an interviewer language and an interviewee language
 * from this list, and every provider reads its per-language settings here.
 *
 * Status values reflect Google Cloud documentation checked in October 2026
 * (Speech-to-Text v2 Chirp 3, Text-to-Speech Chirp 3 HD). Re-check when adding
 * a language: support moves quickly.
 */

export type SupportLevel = "excellent" | "good" | "limited";
export type ProviderStatus = "ga" | "preview" | "unsupported";

export interface LanguageInfo {
  /** BCP-47 code used across the platform (UI, storage, reports). */
  code: string;
  /** English name, shown to consultants. */
  name: string;
  /** Name in the language itself, shown to interviewees. */
  nativeName: string;
  /** Two-letter base language, used for translation and UI strings. */
  base: string;
  stt: {
    /** Code sent to the speech recogniser. */
    code: string;
    status: ProviderStatus;
    /** Used automatically if the recogniser rejects `code` (e.g. a preview locale is withdrawn). */
    fallbackCode?: string;
  };
  tts: {
    /** Locale of the voice used; may differ from `code` when no exact voice exists. */
    voiceLocale: string;
    /** Default Chirp 3 HD voice name. */
    voice: string;
    status: ProviderStatus;
    note?: string;
  };
  support: SupportLevel;
  /** Right-to-left script. */
  rtl?: boolean;
}

export const LANGUAGES: readonly LanguageInfo[] = [
  {
    code: "en-US",
    name: "English (US)",
    nativeName: "English",
    base: "en",
    stt: { code: "en-US", status: "ga" },
    tts: { voiceLocale: "en-US", voice: "en-US-Chirp3-HD-Charon", status: "ga" },
    support: "excellent",
  },
  {
    code: "en-IN",
    name: "English (India)",
    nativeName: "English",
    base: "en",
    stt: { code: "en-IN", status: "ga" },
    tts: { voiceLocale: "en-IN", voice: "en-IN-Chirp3-HD-Charon", status: "ga" },
    support: "excellent",
  },
  {
    code: "hi-IN",
    name: "Hindi",
    nativeName: "हिन्दी",
    base: "hi",
    stt: { code: "hi-IN", status: "ga" },
    tts: { voiceLocale: "hi-IN", voice: "hi-IN-Chirp3-HD-Charon", status: "ga" },
    support: "excellent",
  },
  {
    code: "mr-IN",
    name: "Marathi",
    nativeName: "मराठी",
    base: "mr",
    stt: { code: "mr-IN", status: "preview" },
    tts: { voiceLocale: "mr-IN", voice: "mr-IN-Chirp3-HD-Charon", status: "ga" },
    support: "good",
  },
  {
    code: "es-MX",
    name: "Spanish (Mexico)",
    nativeName: "Español (México)",
    base: "es",
    stt: { code: "es-MX", status: "preview", fallbackCode: "es-US" },
    tts: {
      voiceLocale: "es-US",
      voice: "es-US-Chirp3-HD-Kore",
      status: "ga",
      note: "No Mexico-specific HD voice yet; uses the Latin American (US) Spanish voice.",
    },
    support: "excellent",
  },
  {
    code: "es-US",
    name: "Spanish (Latin America / US)",
    nativeName: "Español",
    base: "es",
    stt: { code: "es-US", status: "ga" },
    tts: { voiceLocale: "es-US", voice: "es-US-Chirp3-HD-Kore", status: "ga" },
    support: "excellent",
  },
  {
    code: "pt-BR",
    name: "Portuguese (Brazil)",
    nativeName: "Português (Brasil)",
    base: "pt",
    stt: { code: "pt-BR", status: "ga" },
    tts: { voiceLocale: "pt-BR", voice: "pt-BR-Chirp3-HD-Kore", status: "ga" },
    support: "good",
  },
  {
    code: "fr-FR",
    name: "French",
    nativeName: "Français",
    base: "fr",
    stt: { code: "fr-FR", status: "ga" },
    tts: { voiceLocale: "fr-FR", voice: "fr-FR-Chirp3-HD-Kore", status: "ga" },
    support: "good",
  },
] as const;

const byCode = new Map(LANGUAGES.map((l) => [l.code, l]));

export function getLanguage(code: string): LanguageInfo {
  const lang = byCode.get(code);
  if (!lang) throw new Error(`Unsupported language: ${code}`);
  return lang;
}

export function isSupportedLanguage(code: string): boolean {
  return byCode.has(code);
}

/** The support level of a language pair is the weaker of the two sides. */
export function pairSupport(a: string, b: string): SupportLevel {
  const order: SupportLevel[] = ["excellent", "good", "limited"];
  const la = getLanguage(a).support;
  const lb = getLanguage(b).support;
  return order[Math.max(order.indexOf(la), order.indexOf(lb))]!;
}

/** True when translation should be switched off (both sides share a base language). */
export function sameLanguage(a: string, b: string): boolean {
  return getLanguage(a).base === getLanguage(b).base;
}
